import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, 
  Search, 
  Calendar, 
  ExternalLink, 
  Globe2, 
  Sparkles, 
  Award, 
  Building2,
  BookmarkCheck
} from 'lucide-react';

// Single feature/publisher image import - update path to your preferred logo asset
// e.g. import IveyLogo from '../../Media/ivey-harvard.png';
import IveyLogo from '../../Media/18.png';

const caseStudiesData = [
  {
    title: "Berrylush: Scaling for Growth",
    publisher: "Emerald Emerging Markets Case Studies",
    distributor: "Emerald Insight",
    year: "2024",
    category: "Strategic Growth & E-Commerce",
    citation: "Emerald Emerging Markets Case Studies (2024)",
    highlight: true,
  },
  {
    title: "DUCKDUCKGO – REINVENTING THE INTERNET SEARCH",
    publisher: "Richard Ivey School of Business",
    distributor: "Jointly Distributed by Harvard Business Publishing",
    year: "2019",
    category: "Strategic Innovation & Tech Governance",
    citation: "Ivey Case Series (Jointly Distributed by Harvard)",
    highlight: true,
  },
  {
    title: "Educomp – Shaping Education in the New Millennium",
    publisher: "Richard Ivey School of Business",
    distributor: "Jointly Distributed by Harvard Business Publishing",
    year: "2013",
    category: "EdTech & Capability Renewal",
    citation: "Ivey Case Series (Jointly Distributed by Harvard)",
    highlight: true,
  },
  {
    title: "The Walt Disney: Strategic Acquisition for Achieving Creativity",
    publisher: "Economic Times Cases in Management",
    distributor: "Economic Times",
    year: "2015",
    category: "M&A and Corporate Strategy",
    citation: "Cases in Management – Economic Times, 12/2015: chapter 10: pages 74–78",
    highlight: false,
  },
  {
    title: "Himbunkar: Turnaround of a Social Public Sector Enterprise",
    publisher: "South Asian Journal of Management",
    distributor: "AMDISA",
    year: "2016",
    category: "Public Sector & Social Enterprise",
    citation: "South Asian Journal of Management, 23(4), 175",
    highlight: false,
  },
];

export default function CaseStudies() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPublisher, setSelectedPublisher] = useState('All');

  const publishers = ['All', 'Richard Ivey / Harvard', 'Emerald Insight', 'Other'];

  const filteredCases = useMemo(() => {
    return caseStudiesData.filter((item) => {
      const matchesSearch =
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.publisher.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.year.includes(searchTerm);

      let matchesPublisher = true;
      if (selectedPublisher === 'Richard Ivey / Harvard') {
        matchesPublisher = item.distributor.includes('Harvard') || item.publisher.includes('Ivey');
      } else if (selectedPublisher === 'Emerald Insight') {
        matchesPublisher = item.publisher.includes('Emerald');
      } else if (selectedPublisher === 'Other') {
        matchesPublisher = !item.distributor.includes('Harvard') && !item.publisher.includes('Emerald');
      }

      return matchesSearch && matchesPublisher;
    });
  }, [searchTerm, selectedPublisher]);

  return (
    <div className="container py-5 min-vh-100 position-relative text-light" style={{ zIndex: 1 }}>
      
      {/* 1. Header Banner */}
      <div className="row align-items-center g-5 mb-5">
        <div className="col-12 col-lg-7" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <BookOpen size={16} className="text-cyan" />
            <span className="small text-light fw-medium">Pedagogy & Applied Executive Research</span>
          </div>

          <h1 className="display-5 fw-bold text-white mb-3">
            Teaching & <span className="text-gradient">Case Studies</span>
          </h1>

          <p className="lead text-secondary fs-6 mb-4 pe-lg-3">
            Empirical teaching cases written on breakthrough technology platforms, global turnarounds, 
            and organizational strategy. Published through the <strong>Richard Ivey School of Business</strong> and 
            distributed globally via <strong>Harvard Business Publishing</strong>.
          </p>

          {/* Quick Metrics */}
          <div className="d-flex flex-wrap gap-3">
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-cyan">Harvard</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Global Distribution</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-warning">Ivey Series</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Case Collection</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-info">Emerald</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Emerging Markets</span>
            </div>
          </div>
        </div>

        {/* Feature Visual / Publisher Emblem */}
        <div className="col-12 col-lg-5 text-center" data-aos="fade-left">
          <div className="position-relative p-4 rounded-4 glass-panel border-glow d-inline-block w-100">
            <div className="d-flex flex-column align-items-center justify-content-center p-3">
              <Globe2 size={42} className="text-cyan mb-3" />
              <h5 className="text-white fw-bold mb-2">Harvard & Ivey Distribution</h5>
              <p className="text-secondary small mb-3">
                Used internationally across executive management classrooms and MBA curricula to examine decision-making under resource constraints and market volatility.
              </p>
              <span 
                className="badge px-3 py-2 rounded-pill small"
                style={{ background: 'rgba(0, 242, 254, 0.1)', color: '#00f2fe', border: '1px solid rgba(0, 242, 254, 0.3)' }}
              >
                Global Executive Pedagogy
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Search & Filter Bar */}
      <div className="p-4 rounded-4 glass-panel mb-5" data-aos="fade-up">
        <div className="row g-3 align-items-center justify-content-between">
          
          {/* Search Box */}
          <div className="col-12 col-md-5">
            <div className="position-relative">
              <Search size={18} className="text-secondary position-absolute top-50 start-0 translate-middle-y ms-3" />
              <input
                type="text"
                placeholder="Search case studies by firm, topic, or year..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control rounded-pill ps-5 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
              />
            </div>
          </div>

          {/* Result Count */}
          <div className="col-12 col-md-auto text-md-end text-secondary small">
            Showing <strong className="text-white">{filteredCases.length}</strong> of {caseStudiesData.length} cases
          </div>
        </div>

        {/* Filter Badges */}
        <div className="d-flex flex-wrap gap-2 mt-3 pt-3 border-top border-secondary border-opacity-25 align-items-center">
          <div className="d-flex align-items-center gap-1 text-secondary small me-2">
            <BookmarkCheck size={14} /> Publisher:
          </div>
          {publishers.map((pub, idx) => {
            const isSelected = selectedPublisher === pub;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setSelectedPublisher(pub)}
                className="btn btn-sm rounded-pill px-3 py-1 transition-all"
                style={{
                  fontSize: '0.8rem',
                  fontWeight: isSelected ? '700' : '500',
                  color: isSelected ? '#0a0f1d' : '#94a3b8',
                  background: isSelected ? 'linear-gradient(135deg, #00f2fe, #4facfe)' : 'rgba(255, 255, 255, 0.05)',
                  border: isSelected ? '1px solid #00f2fe' : '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: isSelected ? '0 0 14px rgba(0, 242, 254, 0.45)' : 'none'
                }}
              >
                {pub}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Case Studies Cards Grid */}
      <div className="row g-4">
        <AnimatePresence>
          {filteredCases.map((item, index) => (
            <motion.div
              layout
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              key={`${item.title}-${index}`}
              className="col-12 col-lg-6"
            >
              <div 
                className="p-4 rounded-4 glass-panel h-100 d-flex flex-column justify-content-between position-relative overflow-hidden"
                style={{
                  borderLeft: item.highlight 
                    ? '4px solid #00f2fe' 
                    : '4px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <div>
                  {/* Category & Year Header */}
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <span 
                      className="badge px-3 py-1 rounded-pill small"
                      style={{
                        background: 'rgba(0, 242, 254, 0.08)',
                        color: '#00f2fe',
                        border: '1px solid rgba(0, 242, 254, 0.2)',
                        fontSize: '0.72rem'
                      }}
                    >
                      {item.category}
                    </span>

                    <span className="small text-warning fw-semibold d-inline-flex align-items-center gap-1">
                      <Calendar size={13} /> {item.year}
                    </span>
                  </div>

                  {/* Title */}
                  <h5 className="text-white fw-bold fs-6 mb-2 lh-base">
                    {item.title}
                  </h5>

                  {/* Distributor / Partner Note */}
                  <p className="text-light opacity-90 small mb-3">
                    <strong>Distributor:</strong> {item.distributor}
                  </p>
                </div>

                {/* Footer Citation Details */}
                <div className="pt-3 border-top border-secondary border-opacity-15 d-flex align-items-center justify-content-between gap-2 text-secondary small">
                  <div className="d-inline-flex align-items-center gap-2 text-truncate">
                    <Building2 size={15} className="text-cyan flex-shrink-0" />
                    <span className="text-truncate">{item.publisher}</span>
                  </div>
                  <span className="badge bg-dark border border-secondary border-opacity-50 text-secondary" style={{ fontSize: '0.7rem' }}>
                    Peer-Reviewed Case
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {filteredCases.length === 0 && (
          <div className="col-12 text-center py-5">
            <p className="text-secondary fs-5 mb-0">No case studies match your search criteria.</p>
          </div>
        )}
      </div>

    </div>
  );
}